import { useState } from "react";
import {
  CANCEL_REASONS,
  ORDER_STAGES,
  canCustomerCancel,
  cents,
  formatRiel,
  formatUsd,
  type CancelReason,
  type Order,
} from "@moomoo/core";
import { ApiError } from "../lib/api.js";
import { errorText, type Translate } from "../lib/i18n.js";
import { CheckIcon, CrossIcon, tileFor } from "./bits.js";

/**
 * Order tracking.
 *
 * Until the office taps Accept the customer sees "waiting", the way a ride
 * app shows a driver being found. Until the driver takes the order out the
 * customer can pull it themselves; after that the page says to call.
 *
 * Retail pays the driver in cash until a Bakong merchant account exists, and
 * the page says so plainly. Nothing here may look like it took money.
 */

const STAGE_LABEL = {
  placed: "stagePlaced",
  confirmed: "stageConfirmed",
  packed: "stagePacked",
  on_the_way: "stageOnTheWay",
  delivered: "stageDelivered",
  cancelled: "stageCancelled",
} as const;

const REASON_KEY: Record<CancelReason, "reasonMistake" | "reasonWrongAddress" | "reasonChangedMind" | "reasonOther"> = {
  mistake: "reasonMistake",
  wrong_address: "reasonWrongAddress",
  changed_mind: "reasonChangedMind",
  other: "reasonOther",
};

export function Track({
  order,
  t,
  lang,
  officePhone,
  onAgain,
  onReorder,
  onCancel,
}: {
  order: Order;
  t: Translate;
  lang: "en" | "kh";
  officePhone: string | null;
  onAgain: () => void;
  onReorder: (order: Order) => void;
  onCancel: (reason: CancelReason) => Promise<void>;
}) {
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState<CancelReason | undefined>();
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();

  // packed is a warehouse step nobody records; the truck is not a warehouse
  const stages = ORDER_STAGES.filter((s) => s !== "cancelled" && s !== "packed");
  const current = order.stage === "cancelled" ? "placed" : order.stage === "packed" ? "confirmed" : order.stage;
  const reached = stages.indexOf(current);
  const total = cents(order.totalCents);
  const paid = order.payment?.status === "paid";
  const cancelled = order.stage === "cancelled";
  const delivered = order.stage === "delivered";
  const waiting = order.stage === "placed";
  const finished = cancelled || delivered;
  const canCancel = canCustomerCancel(order.stage);

  const title = cancelled
    ? t("stageCancelled")
    : waiting
      ? t("waitingTitle")
      : delivered
        ? t("stageDelivered")
        : order.stage === "on_the_way"
          ? t("stageOnTheWay")
          : t("stageConfirmed");
  const sub = cancelled
    ? t("cancelledSub")
    : waiting
      ? t("waitingSub")
      : delivered
        ? t("deliveredSub")
        : order.stage === "on_the_way"
          ? t("onWaySub")
          : t("confirmedSub", order.deliveryDate);

  async function cancelNow() {
    if (!reason) return;
    setBusy(true);
    setProblem(undefined);
    try {
      await onCancel(reason);
      setAsking(false);
    } catch (caught) {
      setProblem(caught instanceof ApiError ? errorText(t, caught) : t("errGeneric"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pad">
      <div className={cancelled ? "orderhero off" : waiting ? "orderhero wait" : "orderhero"} aria-live="polite">
        <div className="disc">
          {cancelled ? (
            <CrossIcon />
          ) : waiting ? (
            <span className="dots" aria-hidden="true">
              <i />
              <i />
              <i />
            </span>
          ) : (
            <CheckIcon />
          )}
        </div>
        <h2>{title}</h2>
        <p>{sub}</p>
        <p className="ref">
          {order.erpName ?? order.id.slice(0, 8)} · {order.deliveryDate}
        </p>
      </div>

      {cancelled ? null : (
        <ul className="trk">
          {stages.map((stage, i) => (
            <li key={stage} className={i < reached ? "done" : i === reached ? "now" : "todo"}>
              <div className="t">{t(STAGE_LABEL[stage])}</div>
            </li>
          ))}
        </ul>
      )}

      {order.lines.map((line) => (
        <div className="row" key={line.code}>
          <span className="thumb">
            <img src={tileFor(line.code)} alt="" width={58} height={58} loading="lazy" />
          </span>
          <span className="mid">
            <span className="t">{lang === "kh" ? line.nameKh : line.nameEn}</span>
            <br />
            <span className="s">
              {formatUsd(cents(line.priceCents))} × {line.qty}
            </span>
          </span>
          <span className="amt">{formatUsd(cents(line.priceCents * line.qty))}</span>
        </div>
      ))}

      <div className="summary">
        <div className="tot">
          <span>{t("subtotal")}</span>
          <span>{formatUsd(cents(order.subtotalCents))}</span>
        </div>
        <div className="tot">
          <span>
            {t("delivery")} · {order.km} km
          </span>
          <span>{order.deliveryCents === 0 ? t("free") : formatUsd(cents(order.deliveryCents))}</span>
        </div>
        <div className="tot grand">
          <span>{t("total")}</span>
          <span>{formatUsd(cents(order.totalCents))}</span>
        </div>
        {order.channel === "wholesale" ? (
          <div className="tot">
            <span>{t("paymentInvoice")}</span>
            <span>{paid ? t("paid") : t("unpaid")}</span>
          </div>
        ) : (
          <div className="tot">
            <span>{t("cashOnDelivery")}</span>
            <span>{paid ? t("paid") : formatRiel(total)}</span>
          </div>
        )}
      </div>
      {order.channel === "wholesale" || paid || cancelled ? null : (
        <p className="note">{t("cashHow", formatUsd(total))}</p>
      )}

      {order.erpName || cancelled ? null : (
        <div className="banner watch" style={{ margin: "12px 0 0" }}>{t("stillBooking")}</div>
      )}

      {canCancel && asking ? (
        <div className="panel cancelsheet">
          <b>{t("cancelAsk")}</b>
          <p className="note" style={{ margin: "4px 0 0" }}>{t("cancelWhyMine")}</p>
          <div className="chips" style={{ padding: "10px 0 0" }}>
            {CANCEL_REASONS.map((r) => (
              <button key={r} type="button" aria-pressed={reason === r} onClick={() => setReason(r)}>
                {t(REASON_KEY[r])}
              </button>
            ))}
          </div>
          {problem ? (
            <div className="field-error" role="alert">
              {problem}
            </div>
          ) : null}
          <div className="picker-actions">
            <button type="button" className="btn quiet" onClick={() => setAsking(false)} disabled={busy}>
              {t("keepOrder")}
            </button>
            <button type="button" className="btn" onClick={() => void cancelNow()} disabled={busy || !reason}>
              {busy ? t("sending") : t("cancelOrder")}
            </button>
          </div>
        </div>
      ) : null}

      {finished ? (
        <button type="button" className="btn" onClick={() => onReorder(order)}>
          {t("reorderThis")}
        </button>
      ) : null}
      <button type="button" className={finished ? "btn quiet" : "btn"} onClick={onAgain}>
        {t("backToShop")}
      </button>

      {canCancel && !asking ? (
        <button type="button" className="textlink" onClick={() => setAsking(true)}>
          {t("cancelOrder")}
        </button>
      ) : null}
      {order.stage === "on_the_way" ? (
        <p className="note" style={{ textAlign: "center" }}>
          {officePhone ? t("needChange", officePhone) : t("needChangeNoPhone")}
        </p>
      ) : null}
    </div>
  );
}
